export const RESULT_ZERO_EPSILON = 1e-9;

const HIDEABLE_RESULT_LABELS = new Set(["windage", "lead"]);
const RANGE_TABLE_SAMPLED_KEYS = new Set(["windage", "lead"]);
const STRICT_NUMBER = /^[+-]?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
const PLACEHOLDER = /^(?:-{1,3}|\u2013|\u2014|\u2026|\.{3}|n\/?a|null|none)$/i;

function checkedEpsilon(epsilon) {
  if (typeof epsilon !== "number" || !Number.isFinite(epsilon) || epsilon < 0) {
    throw new TypeError("Zero epsilon must be a non-negative finite number.");
  }
  return epsilon;
}

function normalizedLabel(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

function classifiedResultNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value)
      ? { kind: "number", value }
      : { kind: "invalid", value: null };
  }

  if (value === null || value === undefined) {
    return { kind: "placeholder", value: null };
  }

  if (typeof value !== "string") {
    return { kind: "invalid", value: null };
  }

  const trimmed = value.trim();
  if (trimmed === "" || PLACEHOLDER.test(trimmed)) {
    return { kind: "placeholder", value: null };
  }

  // U+2212 is common in formatted output, but all other characters must match
  // the complete numeric grammar. In particular, Number-style prefixes such as
  // "1.5 mil" and malformed thousands separators are not accepted.
  const normalized = trimmed.replaceAll("\u2212", "-");
  if (!STRICT_NUMBER.test(normalized)) {
    return { kind: "invalid", value: null };
  }
  const parsed = Number(normalized.replaceAll(",", ""));
  return Number.isFinite(parsed)
    ? { kind: "number", value: parsed }
    : { kind: "invalid", value: null };
}

export function strictFiniteResultNumber(value) {
  const parsed = classifiedResultNumber(value);
  return parsed.kind === "number" ? parsed.value : null;
}

export function isMateriallyNonzeroResultValue(
  value, epsilon = RESULT_ZERO_EPSILON,
) {
  const tolerance = checkedEpsilon(epsilon);
  const parsed = strictFiniteResultNumber(value);
  return parsed !== null && Math.abs(parsed) > tolerance;
}

function resultColumnCount(rows) {
  return rows.reduce((count, row) => (
    Math.max(count, Array.isArray(row?.cells) ? row.cells.length : 0)
  ), 0);
}

function hideableColumnIndices(rows) {
  const indices = new Set();
  for (const row of rows) {
    if (row?.header !== true || !Array.isArray(row.cells)) continue;
    row.cells.forEach((cell, index) => {
      if (HIDEABLE_RESULT_LABELS.has(normalizedLabel(cell))) indices.add(index);
    });
  }
  return indices;
}

function columnIsKnownZeroOrPlaceholder(dataRows, columnIndex, epsilon) {
  return dataRows.every((row) => {
    const parsed = classifiedResultNumber(row.cells[columnIndex]);
    if (parsed.kind === "placeholder") return true;
    if (parsed.kind === "invalid") return false;
    return Math.abs(parsed.value) <= epsilon;
  });
}

/**
 * Returns the column indices safe to render from parsed CGI table rows.
 *
 * Only exact, case-insensitive `Windage` and `Lead` headers are candidates for
 * removal. An unrecognized data value keeps its column visible so a legacy
 * formatting change cannot silently discard information.
 */
export function visibleResultColumnIndices(
  rows, epsilon = RESULT_ZERO_EPSILON,
) {
  const tolerance = checkedEpsilon(epsilon);
  const tableRows = Array.isArray(rows) ? rows : [];
  const columnCount = resultColumnCount(tableRows);
  const hideable = hideableColumnIndices(tableRows);
  const dataRows = tableRows.filter((row) => (
    row?.header !== true && Array.isArray(row?.cells)
  ));

  return Array.from({ length: columnCount }, (_, index) => index)
    .filter((index) => !hideable.has(index)
      || !columnIsKnownZeroOrPlaceholder(dataRows, index, tolerance));
}

function sampledValueRecords(sampledCases) {
  return (Array.isArray(sampledCases) ? sampledCases : []).flatMap((sampledCase) => {
    const values = sampledCase?.values;
    if (values instanceof Map) return [...values.values()];
    return values !== null && typeof values === "object" ? Object.values(values) : [];
  });
}

function sampledRecordValue(record, key) {
  if (record instanceof Map) return record.get(key);
  return record !== null && typeof record === "object" ? record[key] : undefined;
}

export function hasMaterialRangeTableSampledValue(
  sampledCases, key, epsilon = RESULT_ZERO_EPSILON,
) {
  if (!RANGE_TABLE_SAMPLED_KEYS.has(key)) {
    throw new TypeError(
      "Sampled range-table key must identify total windage or lead.",
    );
  }
  const tolerance = checkedEpsilon(epsilon);
  return sampledValueRecords(sampledCases).some((record) => {
    const parsed = classifiedResultNumber(sampledRecordValue(record, key));
    if (parsed.kind === "invalid") return true;
    return parsed.kind === "number" && Math.abs(parsed.value) > tolerance;
  });
}

export function rangeTableSampledColumnVisibility(
  sampledCases, epsilon = RESULT_ZERO_EPSILON,
) {
  const tolerance = checkedEpsilon(epsilon);
  return Object.freeze({
    windage: hasMaterialRangeTableSampledValue(sampledCases, "windage", tolerance),
    lead: hasMaterialRangeTableSampledValue(sampledCases, "lead", tolerance),
  });
}
